import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, Loader2, Landmark, MessageCircle } from "lucide-react";
import { useCart, API } from "../context/CartContext";

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { clearCart, config } = useCart();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const gateway = params.get("gateway");
  const [state, setState] = useState(gateway === "midtrans" ? "paid" : gateway === "bank" ? "bank" : "checking");
  const [amountIdr, setAmountIdr] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (gateway === "bank" && orderId) {
      axios.get(`${API}/orders/${orderId}`).then((r) => setAmountIdr(r.data.total_idr)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateway, orderId]);

  useEffect(() => {
    if (!sessionId) return;
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); clearCart(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("pending"); return; }
      } catch {}
      if (attempts.current < 8) timer = setTimeout(poll, 2500);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center px-6">
      <div data-testid="payment-result" className="bg-white rounded-3xl p-10 max-w-md w-full text-center border border-brand-green/15 shadow-xl">
        {state === "checking" && (
          <>
            <Loader2 size={56} className="mx-auto text-brand-orange animate-spin" />
            <h1 className="font-heading text-3xl text-brand-dark uppercase mt-6">{t("payment.checking")}</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={64} className="mx-auto text-brand-green" />
            <h1 data-testid="payment-success-title" className="font-heading text-4xl text-brand-dark uppercase mt-6">{t("payment.successTitle")}</h1>
            <p className="text-sm text-brand-brown mt-3">{t("payment.successDesc")}</p>
            {orderId && (
              <p className="mt-4 text-xs font-bold text-brand-brown bg-brand-cream rounded-full px-4 py-2 inline-block">
                {t("payment.orderId")}: <span data-testid="payment-order-id">{orderId}</span>
              </p>
            )}
          </>
        )}
        {state === "bank" && (
          <>
            <Landmark size={56} className="mx-auto text-brand-green" />
            <h1 data-testid="bank-transfer-title" className="font-heading text-4xl text-brand-dark uppercase mt-6">{t("payment.bankTitle")}</h1>
            <p className="text-sm text-brand-brown mt-3">{t("payment.bankDesc")}</p>
            <div className="mt-6 bg-brand-cream rounded-2xl p-6 text-start space-y-3">
              <div className="flex justify-between text-sm"><span className="text-brand-brown font-semibold">{t("payment.bankName")}</span><span className="font-bold text-brand-dark">{config.bank?.bank_name}</span></div>
              <div className="flex justify-between text-sm"><span className="text-brand-brown font-semibold">{t("payment.accountNumber")}</span><span data-testid="bank-account-number" className="font-bold text-brand-dark">{config.bank?.account_number}</span></div>
              <div className="flex justify-between text-sm"><span className="text-brand-brown font-semibold">{t("payment.accountName")}</span><span className="font-bold text-brand-dark">{config.bank?.account_name}</span></div>
              {amountIdr != null && (
                <div className="flex justify-between text-sm border-t border-brand-green/15 pt-3"><span className="text-brand-brown font-semibold">{t("payment.amount")}</span><span data-testid="bank-amount" className="font-heading text-2xl text-brand-orange">Rp {amountIdr.toLocaleString("id-ID")}</span></div>
              )}
            </div>
            {orderId && (
              <p className="mt-4 text-xs font-bold text-brand-brown bg-brand-cream rounded-full px-4 py-2 inline-block">
                {t("payment.orderId")}: <span data-testid="payment-order-id">{orderId}</span>
              </p>
            )}
            <a data-testid="bank-confirm-whatsapp" target="_blank" rel="noopener noreferrer"
              href={`https://wa.me/${config.whatsapp_number}?text=${encodeURIComponent(`${t("wa.bankConfirm")} ${orderId}`)}`}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#25D366] text-white font-bold text-sm uppercase tracking-wide hover:opacity-90 transition-opacity">
              <MessageCircle size={18} /> {t("payment.confirmWa")}
            </a>
          </>
        )}
        {state === "pending" && (
          <>
            <Clock size={64} className="mx-auto text-brand-orange" />
            <h1 className="font-heading text-4xl text-brand-dark uppercase mt-6">{t("payment.pendingTitle")}</h1>
            <p className="text-sm text-brand-brown mt-3">{t("payment.pendingDesc")}</p>
          </>
        )}
        <button data-testid="payment-home-button" onClick={() => navigate("/")}
          className="mt-8 px-8 py-3 rounded-full bg-brand-orange text-white font-bold text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors">
          {t("payment.home")}
        </button>
        {orderId && (
          <button data-testid="payment-track-button" onClick={() => navigate(`/order/${orderId}`)}
            className="mt-3 block w-full px-8 py-3 rounded-full border-2 border-brand-dark text-brand-dark font-bold text-sm uppercase tracking-wide hover:bg-brand-yellow transition-colors">
            {t("track.button")}
          </button>
        )}
      </div>
    </div>
  );
}
